import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ReactNode, useEffect } from 'react';
import classNames from 'classnames';
import { linkBridge } from '@webview-bridge/web';
import { isFullFooter, isFullHeader, isItauTemplate, isSimpleHeader, isWithoutHeader, pageNames } from '@common/routes/pageNames';
import {
  Button,
  Drawer,
  Footer,
  Header,
  IconChevronLeft,
  IconX,
  LogoBadgetAC,
  LogoBadgetCP,
  LogoTertiaryAC,
  LogoBadgeWhiteStrokeDefaultAC,
  NavbarVertical,
  NotificationsBar,
  ProfileNav,
  Text,
  Conditional,
} from '@consumidor-positivo/aurora';
import { usePrivatePageHook } from './hooks/index';

import { CpAppNavigation } from './components/CpAppNavigation';
import { CpFooter } from './components/CpFooter';
import { CpHeader } from './components/CpHeader';
import NotificationFakeDoor from './components/NotificationFakeDoor';
import { CustomExperienceService, NubankService } from '@libs/renegkit';
import { getPartnerImage } from '@libs/utils/helpers/getPartnerImage';
import { useDebtsContext } from '@common/contexts/DebtsContext/useDebtsContext';
import itauWhiteLogo from '@common/assets/partners/itau-white-logo.svg';

import { EmailResubscriptionSwitch } from '@libs/acp/components/exported/EmailResubscriptionSwitch';

import './styles.scss';

type PrivatePageTemplateProps = {
  children: ReactNode;
};

export default function PrivatePageTemplate({ children }: PrivatePageTemplateProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const {
    dataSourceNavbar,
    dataSourceNavbarVertical,
    drawerOpen,
    handleOpenDrawer,
    onClickBackMenu,
    onClickLogout,
    partnerImage,
    firstName,
    fullName,
    recentNotifications,
    handleOpenNotification,
    showNotificationFakeDoor,
    setShowNotificationFakeDoor,
    showNotificationIcon,
    setShowNotificationIcon,
    isNCADCheckout,
    isNCADGeneral,
    hasFullPermissions,
    hasPermissionToAccessFullHeader,
    isMagalu,
  } = usePrivatePageHook();
  const { nubankExperience } = useDebtsContext();

  const privateClass = classNames('private-page', {
    'private-page--cp': import.meta.env.VITE_CUSTOM_BUILD && !location.pathname.includes('/acordo'),
    'private-page--without-header': isWithoutHeader(location.pathname),
  });

  useEffect(() => {
    if (!import.meta.env.VITE_CUSTOM_BUILD) return;

    const bridge = linkBridge({ throwOnError: true, timeout: 60000 });

    if (bridge.isWebViewBridgeAvailable) {
      window.isNativeApp = true;
      window.bridge = bridge.loose;
    }
  }, []);

  //#region Checkout wrapped Itaú
  const classItau = classNames('private-page__header', {
    'private-page__header--itau': isItauTemplate(location.pathname) && !isMagalu,
  });
  //#endregion

  if (location.pathname.includes('checkout/')) {
    return children;
  }

  return (
    <>
      <Conditional
        condition={isFullHeader(location.pathname) && !isSimpleHeader(location.pathname)}
        renderIf={
          import.meta.env.VITE_CUSTOM_BUILD ? (
            <CpHeader username={fullName} />
          ) : (
            <Header.Root position="fixed">
              <Header.Badges limitBadgeWidth={false}>
                <Header.Button onClick={() => navigate(pageNames.myDebts.path)}>
                  <Header.Logo renderDesktop={<LogoTertiaryAC />} renderMobile={<LogoBadgetAC />} />
                </Header.Button>
                <Conditional
                  condition={nubankExperience === NubankService.NUBANK_CAMPAIGN_LP}
                  renderIf={
                    <div className="au-logo">
                      <img className="img-badges-partner" src={getPartnerImage('nubank')} alt={`Logo nubank`} />
                    </div>
                  }
                />
              </Header.Badges>
              <Header.Navigation>
                <Header.Navbar
                  data={dataSourceNavbar}
                  renderItem={(item) => (
                    <Header.NavbarLink key={item.name} active={item.active} name={item.name} dropdown={item?.dropdown} onClick={item.onClick} />
                  )}
                />
                <Header.Actions divider>
                  {/* <Conditional
                    condition={showNotificationIcon}
                    renderIf={
                      <div className="notifications-icon" onClick={handleOpenNotification}>
                        <Text weight="bold">1</Text>
                      </div>
                    }
                  /> */}

                  <Header.Profile
                    onClickMenu={() => handleOpenDrawer('menu')}
                    fullName={fullName || ''}
                    notifications={{
                      visible: !CustomExperienceService.getLeadsCustomExperience(),
                      onClick: () => handleOpenDrawer('notification'),
                    }}
                  />
                </Header.Actions>
              </Header.Navigation>

              <Drawer
                isOpen={drawerOpen.menu}
                handleOpen={() => handleOpenDrawer('menu')}
                renderHeader={<ProfileNav name={firstName || ''} fullName={fullName || ''} />}
                renderContent={
                  <NavbarVertical
                    data={dataSourceNavbarVertical}
                    renderItem={(link) => {
                      const dropdown = link.dropdown?.map((item) => ({
                        ...item,
                        onClick: () => {
                          handleOpenDrawer('menu');
                          item.onClick?.();
                        },
                      }));

                      return (
                        <NavbarVertical.Link
                          key={link.name}
                          active={link.active}
                          Icon={link.Icon}
                          name={link.name}
                          dropdown={dropdown}
                          onClick={() => {
                            handleOpenDrawer('menu');
                            link.onClick?.();
                          }}
                        />
                      );
                    }}
                    renderActions={() => (
                      <Button type="outlined" expand="x" onClick={onClickLogout}>
                        Sair
                      </Button>
                    )}
                  />
                }
              />
              <Drawer
                isOpen={drawerOpen.notification}
                handleOpen={() => handleOpenDrawer('notification')}
                renderHeader={
                  <Text as="h3" variant="heading-small" weight="bold">
                    Notificações
                  </Text>
                }
                renderContent={
                  <NotificationsBar.Root
                    renderRecents={() => (
                      <>
                        <EmailResubscriptionSwitch absolutePosition={true} id="notificationResub" location="notificationBar" />

                        <NotificationsBar.List
                          title="Recentes"
                          dataSource={recentNotifications}
                          renderItem={(item) => (
                            <NotificationsBar.Link
                              key={`notification-item-${item.title}`}
                              title={item.title}
                              Icon={item.Icon}
                              createdAt={item.createdAt}
                              onClick={() => {
                                handleOpenDrawer('notification');
                                item.onClick();
                              }}
                            />
                          )}
                        />

                        <Conditional
                          condition={showNotificationFakeDoor}
                          renderIf={
                            <NotificationFakeDoor
                              setShowNotificationIcon={setShowNotificationIcon}
                              setShowNotificationFakeDoor={setShowNotificationFakeDoor}
                            />
                          }
                        />
                      </>
                    )}
                  />
                }
              />
            </Header.Root>
          )
        }
      />

      <Conditional
        condition={isSimpleHeader(location.pathname)}
        renderIf={
          import.meta.env.VITE_CUSTOM_BUILD ? (
            <Header.Root position="fixed">
              <Header.Button onClick={() => (window.location.href = '/minhas-dividas')}>
                <IconChevronLeft />
              </Header.Button>
              <Header.Badges>
                <LogoBadgetCP />
                <Conditional
                  condition={!!params?.partner}
                  renderIf={
                    <div className="au-logo">
                      <img className="img-badges-partner" src={partnerImage} alt={`Logo ${params.partner}`} />
                    </div>
                  }
                />
              </Header.Badges>
              <Header.Button onClick={() => (window.location.href = '/minhas-dividas')}>
                <IconX />
              </Header.Button>
            </Header.Root>
          ) : (
            <div className={classItau}>
              <Header.Root position="fixed">
                <Header.Button onClick={onClickBackMenu}>
                  <Conditional condition={!isNCADCheckout && !isNCADGeneral} renderIf={<IconChevronLeft />} />
                </Header.Button>
                <Header.Badges>
                  <Conditional
                    condition={isItauTemplate(location.pathname) && !isMagalu}
                    renderIf={
                      <>
                        <Header.Button onClick={isNCADCheckout || isNCADGeneral ? () => {} : () => navigate(pageNames.myDebts.path)}>
                          <LogoBadgeWhiteStrokeDefaultAC />
                        </Header.Button>
                        <div className="au-logo">
                          <img className="img-itau-partner" src={itauWhiteLogo} alt={`Logo ${params.partner}`} />
                        </div>
                      </>
                    }
                    renderElse={
                      <>
                        <Header.Button onClick={isNCADCheckout || isNCADGeneral ? () => {} : () => navigate(pageNames.myDebts.path)}>
                          <LogoBadgetAC />
                        </Header.Button>
                        <Conditional
                          condition={!!params?.partner}
                          renderIf={
                            <div className="au-logo">
                              <img className="img-badges-partner" src={partnerImage} alt={`Logo ${params.partner}`} />
                            </div>
                          }
                        />
                      </>
                    }
                  />
                </Header.Badges>

                <Conditional
                  condition={location.pathname.includes('acordo-fechado') && !isNCADCheckout && !isNCADGeneral}
                  renderIf={
                    <Header.Button onClick={() => navigate(pageNames.myDebts.path)}>
                      <IconX />
                    </Header.Button>
                  }
                />
              </Header.Root>
            </div>
          )
        }
      />

      <Conditional
        condition={
          (!isFullHeader(location.pathname) && !isSimpleHeader(location.pathname) && !isWithoutHeader(location.pathname)) ||
          (!hasFullPermissions && !hasPermissionToAccessFullHeader)
        }
        renderIf={
          <Header.Root position="fixed">
            <Header.Button onClick={isNCADGeneral ? () => {} : () => navigate(pageNames.myDebts.path)}>
              <Header.Logo>
                <LogoTertiaryAC />
              </Header.Logo>
            </Header.Button>
          </Header.Root>
        }
      />
      <main role="main" className={privateClass}>
        <div className="private-page__container">{children}</div>

        {/* <LogoutModal
          show={showLogoutModal}
          onLogout={onClickLogout}
          onNotLogout={onClickNotLogout}
        /> */}
      </main>
      <Conditional
        condition={isFullFooter(location.pathname)}
        renderIf={
          import.meta.env.VITE_CUSTOM_BUILD ? (
            <>
              <CpFooter />
              <CpAppNavigation />
            </>
          ) : (
            <Footer
              logo={<LogoTertiaryAC />}
              socialLinks={{
                facebook: 'https://www.facebook.com/oficialacordocerto/',
                instagram: 'https://www.instagram.com/acordocerto',
                linkedin: 'https://www.linkedin.com/company/acordo-certo/',
                youtube: 'https://www.youtube.com/c/AcordoCertoOficial',
                tiktok: 'https://www.tiktok.com/@acordocerto',
              }}
              certificates={['ra1000', 'fintech2022', 'fintech2023', 'scaleup', 'quintessa']}
              copyrights={
                <>
                  © Copyright 2025 Consumidor Positivo Ltda.
                  <br />
                  Todos os direitos reservados / CNPJ: 30.247.128/0001-15
                </>
              }
            />
          )
        }
      />
    </>
  );
}
